import { Grid } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  DateRangeParams,
  DateRangePreset,
} from "src/data/admin/types/analytics";
import { AnalyticsHeader } from "./components/AnalyticsHeader";
import { AnalyticsStatsRow } from "./components/AnalyticsStatsRow";
import { BookingsTrend } from "./components/BookingsTrend";
import { PopularBookingTimes } from "./components/PopularBookingTimes";
import { PartySizeDistribution } from "./components/PartySizeDistribution";
import { BookingSources } from "./components/BookingSources";
import { DayOfWeekPerformance } from "./components/DayOfWeekPerformance";
import { DealPerformanceSection } from "./components/DealPerformanceSection";
import { EventPerformanceSection } from "./components/EventPerformanceSection";
import {
  useAnalyticsOverview,
  useBookingsTrend,
  usePopularTimes,
  usePartySizeDistribution,
  useBookingSources,
  useDayOfWeekPerformance,
} from "src/data/admin/queries/analytics";

// Monday-first weeks (matches the analytics offer-performance contract).
dayjs.extend(isoWeek);

// Maps a calendar preset to the dayjs unit used by startOf/endOf. "week" uses
// isoWeek so the period runs Monday→Sunday regardless of locale.
const UNIT_BY_PRESET: Record<
  Exclude<DateRangePreset, "custom">,
  "isoWeek" | "month" | "year"
> = {
  week: "isoWeek",
  month: "month",
  year: "year",
};


const AnalyticsPage = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();

  const [preset, setPreset] = useState<DateRangePreset>("week");
  const [customStart, setCustomStart] = useState<Dayjs>(dayjs().startOf("isoWeek"));
  const [customEnd, setCustomEnd] = useState<Dayjs>(dayjs().endOf("isoWeek"));

  // Resolve the active preset to a concrete [start, end] window. Calendar
  // presets span the full current period (start→end of the unit), so the
  // remainder of the period — upcoming bookings/events — is included.
  const start =
    preset === "custom"
      ? customStart
      : dayjs().startOf(UNIT_BY_PRESET[preset]);
  const end =
    preset === "custom" ? customEnd : dayjs().endOf(UNIT_BY_PRESET[preset]);

  const trendDays = end.diff(start, "day");

  const range: DateRangeParams = {
    restaurantId: restaurantId ?? "",
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
  };

  const { data: overview } = useAnalyticsOverview(range);
  const { data: trend } = useBookingsTrend(range);
  const { data: popularTimes } = usePopularTimes(range);
  const { data: partySize } = usePartySizeDistribution(range);
  const { data: sources } = useBookingSources(range);
  const { data: dayOfWeek } = useDayOfWeekPerformance(range);

  return (
    <Grid container spacing={2} sx={{ p: 3 }}>
      <Grid item xs={12}>
        <AnalyticsHeader
          preset={preset}
          customStart={customStart}
          customEnd={customEnd}
          onPresetChange={setPreset}
          onCustomRangeChange={(start, end) => {
            setCustomStart(start);
            setCustomEnd(end);
          }}
        />
      </Grid>

      {/* Stats row */}
      {overview && (
        <Grid item xs={12}>
          <AnalyticsStatsRow data={overview} />
        </Grid>
      )}

      {/* Trend chart + Popular times */}
      {trend && (
        <Grid item xs={12} md={6}>
          <BookingsTrend data={trend} days={trendDays} />
        </Grid>
      )}
      {popularTimes && (
        <Grid item xs={12} md={6}>
          <PopularBookingTimes data={popularTimes} />
        </Grid>
      )}

      {/* Per-offer performance: covers by weekday vs. previous period */}
      <Grid item xs={12}>
        <DealPerformanceSection range={range} />
      </Grid>
      <Grid item xs={12}>
        <EventPerformanceSection range={range} />
      </Grid>

      {/* Party size | Booking sources | Day of week */}
      {partySize && (
        <Grid item xs={12} md={6}>
          <PartySizeDistribution data={partySize} />
        </Grid>
      )}
      {/* {sources && (
        <Grid item xs={12} md={4}>
          <BookingSources data={sources} />
        </Grid>
      )} */}
      {dayOfWeek && (
        <Grid item xs={12} md={6}>
          <DayOfWeekPerformance data={dayOfWeek} />
        </Grid>
      )}


    </Grid>
  );
};

export default AnalyticsPage;
